<!--
	Технические работы.

	Закрывает приложение целиком: пока идут работы, сервер отвечает отказом на
	всё, кроме входа, и нажимать под этим экраном нечего. Администрация его не
	видит — она в это время и чинит.

	Картинка лежит в статике, а не приходит с сервера: во время работ сервер
	отвечает отказом почти на всё, и картинке неоткуда было бы взяться.

	Отсчёт показывается, только если названо время окончания. По нулю экран сам
	не открывается: закончились работы или нет, решает сервер, а не часы на
	устройстве, — поэтому по нулю мы просто спрашиваем ещё раз.
-->
<script lang="ts">
	import { Icon } from '@andresot/ui';
	import type { MaintenanceState } from '@andresot/protocol/envelope';

	interface Props {
		/* Не `state`: имя `$state` — руна, и переменная с таким именем её закрывает. */
		works: MaintenanceState;
		/** Отсчёт дошёл до нуля — пора спросить сервер, кончились ли работы. */
		onfinished?: () => void;
	}

	let { works, onfinished }: Props = $props();

	let now = $state(Date.now());
	let finished = false;

	$effect(() => {
		if (works.endsAt === null) return;

		finished = false;
		const id = setInterval(() => (now = Date.now()), 1_000);
		return () => clearInterval(id);
	});

	let left = $derived(works.endsAt === null ? 0 : Math.max(0, works.endsAt - now));

	$effect(() => {
		if (works.endsAt === null || left > 0 || finished) return;
		finished = true;
		onfinished?.();
	});

	/** ч:мм:сс — часы не сворачиваются в сутки: «01:24:37» понятнее «1 ч». */
	let countdown = $derived.by(() => {
		const total = Math.floor(left / 1_000);
		const hours = Math.floor(total / 3_600);
		const minutes = Math.floor((total % 3_600) / 60);
		const seconds = total % 60;
		return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
	});
</script>

<div class="screen" role="alertdialog" aria-modal="true" aria-label={works.title}>
	<div class="card">
		<!--
			Ширина и высота проставлены: без них карточка подпрыгивает, когда
			картинка догружается. Подписи нет — рядом стоит заголовок «Технические
			работы», и читалка экрана повторяла бы то же самое второй раз.
		-->
		<img
			class="art"
			src="/maintenance-mascot.webp"
			alt=""
			width="560"
			height="605"
			decoding="async"
		/>

		<h1 class="title">{works.title}</h1>
		<p class="subtitle"><b>ANDRESOT</b> временно недоступен</p>
		<p class="text">{works.message}</p>

		{#if works.endsAt !== null}
			<div class="countdown">
				<span class="clock"><Icon name="clock" size={40} strokeWidth={1.8} /></span>
				<span class="countdown-body">
					<span class="countdown-label">До окончания работ:</span>
					<strong class="countdown-value" aria-label="До окончания работ">{countdown}</strong>
				</span>
			</div>
		{/if}

		<p class="footer">Пожалуйста, дождитесь завершения работ</p>
	</div>
</div>

<style>
	.screen {
		position: fixed;
		inset: 0;
		z-index: var(--z-maintenance);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-l);
		/* Приложение под экраном видно, но приглушено: человеку спокойнее, когда
		   понятно, что он не потерял свои переписки. */
		background: var(--background-overlay-hard);
		backdrop-filter: blur(10px);
	}

	.card {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-s);
		width: 100%;
		max-width: 420px;
		padding: var(--spacing-2xl) var(--spacing-xl) var(--spacing-xl);
		border-radius: var(--radius-2xl);
		background: var(--background-surface);
		box-shadow: var(--shadow-l);
		text-align: center;
	}

	.art {
		width: 250px;
		max-width: 70%;
		height: auto;
	}

	.title {
		font: var(--text-title);
	}

	.subtitle {
		font: var(--text-body);
	}

	.subtitle b {
		color: var(--status-success);
		font-weight: var(--font-weight-semibold);
	}

	.text {
		color: var(--text-secondary);
		font: var(--text-secondary-line);
	}

	/* Отсчёт отбит линиями сверху и снизу — это единственное на экране, что
	   меняется, и глазу нужно за что зацепиться. */
	.countdown {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-m);
		width: 100%;
		margin-top: var(--spacing-s);
		padding: var(--spacing-m) 0;
		border-top: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08));
		border-bottom: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08));
	}

	.clock {
		display: flex;
		color: var(--status-success);
	}

	.countdown-body {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--spacing-2xs);
	}

	.countdown-label {
		color: var(--text-secondary);
		font: var(--text-secondary-line);
	}

	.countdown-value {
		color: var(--status-success);
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		/* Моноширинные цифры: иначе на каждой секунде строка дёргается. */
		font-variant-numeric: tabular-nums;
		letter-spacing: 1px;
	}

	.footer {
		color: var(--text-secondary);
		font: var(--text-secondary-line);
	}

	@media (max-width: 900px) {
		.card {
			padding: var(--spacing-xl) var(--spacing-l) var(--spacing-l);
		}
	}
</style>
